import React from "react";
import { Box, Button, CircularProgress, useTheme } from "@mui/material";
import { WizardFooterRow } from "../style";

interface WizardFooterSecondaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

interface WizardFooterProps {
  resetLabel: string;
  nextLabel: string;
  backLabel?: string;
  onReset: () => void;
  onNext: () => void;
  onBack?: () => void;
  showBack?: boolean;
  secondaryAction?: WizardFooterSecondaryAction;
  isCompleting?: boolean;
}

const WizardFooter: React.FC<WizardFooterProps> = ({
  resetLabel,
  nextLabel,
  backLabel,
  onReset,
  onNext,
  onBack,
  showBack = false,
  secondaryAction,
  isCompleting = false,
}) => {
  const theme = useTheme();

  return (
    <WizardFooterRow>
      {showBack && backLabel && (
        <Button
          onClick={onBack}
          variant="outlined"
          disabled={isCompleting}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            "&:hover": {
              borderColor: theme.palette.text.primary,
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          {backLabel}
        </Button>
      )}
      <Box sx={{ flex: 1 }} />
      <Button
        onClick={onReset}
        variant="text"
        disabled={isCompleting}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          color: theme.palette.text.primary,
          textDecoration: "underline",
          "&:hover": {
            textDecoration: "underline",
            backgroundColor: "transparent",
          },
        }}
      >
        {resetLabel}
      </Button>
      {secondaryAction && (
        <Button
          onClick={secondaryAction.onClick}
          variant="outlined"
          disabled={secondaryAction.disabled || isCompleting}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            "&:hover": {
              borderColor: theme.palette.text.primary,
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          {secondaryAction.label}
        </Button>
      )}
      <Button
        onClick={onNext}
        variant="contained"
        disabled={isCompleting}
        endIcon={
          isCompleting ? (
            <CircularProgress size={16} sx={{ color: "inherit" }} />
          ) : undefined
        }
        sx={{
          textTransform: "none",
          fontWeight: 700,
          borderRadius: "8px",
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
        }}
      >
        {nextLabel}
      </Button>
    </WizardFooterRow>
  );
};

export default WizardFooter;
